import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingBasket } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { useCart } from "@/context/CartContext";

export function ProductCard({ product, index = 0 }) {
  const { addToCart } = useCart();
  const reduceMotion = useReducedMotion();
  const available = product.isAvailable !== false;

  const handleAdd = () => {
    addToCart(product);
  };

  return (
    <motion.article
      initial={reduceMotion ? false : { opacity: 0, y: 28, scale: 0.98 }}
      whileInView={reduceMotion ? {} : { opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.55, delay: index * 0.06 }}
      whileHover={reduceMotion ? {} : { y: -8 }}
      className="group relative overflow-hidden rounded-[2rem] border border-[#EAEAE6] bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-shadow duration-300 hover:shadow-[0_24px_52px_rgb(0,0,0,0.1)]"
      data-testid={`product-card-${product.slug}`}
    >
      <div className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full border border-[#E8C872]/40 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
      <Link data-testid={`product-image-link-${product.slug}`} to={`/product/${product.slug}`} className="block aspect-[4/3] overflow-hidden bg-[#F0F0EA]">
        <motion.img data-testid={`product-image-${product.slug}`} src={product.image} alt={product.name} className="h-full w-full object-cover object-center" whileHover={reduceMotion ? {} : { scale: 1.08 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} loading="lazy" />
      </Link>
      <div className="p-6">
        <div className="mb-4 flex items-start justify-between gap-3">
          <span data-testid={`product-badge-${product.slug}`} className="rounded-full bg-[#F0F0EA] px-3 py-1 text-xs font-medium text-[#4A3B32]">{product.badge}</span>
          {available ? (
            <span data-testid={`product-price-${product.slug}`} className="text-lg font-semibold text-[#2A4B3C]">{product.priceLabel}</span>
          ) : (
            <span data-testid={`product-price-hidden-${product.slug}`} className="text-xs font-medium text-[#6B6B66]">{product.comingSoon ? "Coming soon" : "Seasonal"}</span>
          )}
        </div>
        <h3 data-testid={`product-name-${product.slug}`} className="text-2xl font-normal tracking-tight text-[#2C2C2A]">{product.name}</h3>
        <p data-testid={`product-description-${product.slug}`} className="mt-3 min-h-[54px] text-sm leading-relaxed text-[#6B6B66]">{product.shortDescription}</p>
        <div className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-500 group-hover:grid-rows-[1fr]">
          <p data-testid={`product-hover-detail-${product.slug}`} className="overflow-hidden text-sm leading-relaxed text-[#4F795B]">{product.feature}</p>
        </div>
        {available ? <p data-testid={`product-pack-size-${product.slug}`} className="mt-4 text-sm font-medium text-[#4A3B32]">{product.packSize}</p> : <p data-testid={`product-unavailable-note-${product.slug}`} className="mt-4 text-sm font-medium text-[#C84B31]">{product.comingSoon ? "Coming soon" : "Currently unavailable"}</p>}
        <div className="mt-6 flex flex-col gap-3">
          <button
            data-testid={`add-to-cart-button-${product.slug}`}
            type="button"
            onClick={handleAdd}
            disabled={!available}
            className={`premium-button inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium transition-all duration-300 ${available ? "bg-[#2A4B3C] text-white hover:-translate-y-0.5 hover:bg-[#1E362B]" : "cursor-not-allowed bg-[#EAEAE6] text-[#6B6B66]"}`}
          >
            <ShoppingBasket className="h-4 w-4" /> {available ? "Add to Cart" : product.comingSoon ? "Coming Soon" : "Out of Season"}
          </button>
          <Link data-testid={`view-product-link-${product.slug}`} to={`/product/${product.slug}`} className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#D5D5D0] px-5 py-3 text-sm font-medium text-[#2A4B3C] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#FAFAF8]">
            View <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </motion.article>
  );
}